import { classNames } from '@fullstory/util/fmt';
import React, { PropsWithChildren } from 'react';
import { identifyComponent } from '../../util';
import css, { ClassNames } from './index.module.scss';

/* eslint-disable jsx-a11y/mouse-events-have-key-events */

type VerticalAlignScale = 'top' | 'center' | 'bottom';
type AlignScale = 'start' | 'center' | 'end';
type WidthTypes = 'quarter' | 'third' | 'half' | 'twoThirds';
type MarginSizeScale = 'small' | 'medium' | 'large';
type BreakPointScale = 'small' | 'medium' | 'large';

interface ElementBase {
  testId?: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  onMouseOver?: (e: React.MouseEvent) => void;
}

interface ContainerProps extends ElementBase {
  verticalAlign?: VerticalAlignScale;
  align?: AlignScale;
  gapless?: boolean;
  marginSize?: MarginSizeScale;
}

export interface ColumnProps extends ElementBase {
  /** Horizontal size of the column in relation to the parent */
  width?: WidthTypes;
  /** Determines what breakpoint to respect width */
  breakpoint?: BreakPointScale;
}

export const Container: React.FunctionComponent<PropsWithChildren<ContainerProps>> = ({
  testId = 'container',
  className,
  onClick,
  onMouseOver,
  verticalAlign,
  align,
  gapless,
  marginSize = 'small',
  children,
}) => (
  /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
  <div
    data-component="Container"
    data-testid={testId}
    className={classNames(css.compContainer, className, {
      [css[`vertical-${verticalAlign}` as ClassNames]]: !!verticalAlign,
      [css[`margin-${marginSize}` as ClassNames]]: !!marginSize,
      [css.start]: align === 'start',
      [css.centered]: align === 'center',
      [css.end]: align === 'end',
      [css.gapless]: !!gapless,
    })}
    onClick={onClick}
    onMouseOver={onMouseOver}
  >
    {children}
  </div>
);

export const Column: React.FunctionComponent<PropsWithChildren<ColumnProps>> = ({
  testId = 'column',
  className,
  width,
  breakpoint,
  onClick,
  onMouseOver,
  children,
}) => (
  /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
  <div
    data-component="Column"
    data-testid={testId}
    className={classNames(css.compColumn, {
      [css[width as ClassNames]]: !!width,
      [css[`bp-${breakpoint}` as ClassNames]]: !!breakpoint,
    })}
    onClick={onClick}
    onMouseOver={onMouseOver}
    {...identifyComponent('column')}
  >
    <div className={className}>{children}</div>
  </div>
);
